import { useFormikContext } from 'formik';
import React, { useState, useRef, useEffect } from 'react';
import { useTranslation } from 'react-i18next';

const AgreementUpload = ({
  fieldName = 'rentAgreementFile',              // 'rentAgreementFile' or 'subTenantAgreementImage'
  oldFieldName = 'oldRentAgreementExpiry',      // 'oldRentAgreementExpiry' or 'oldSubTenantAgreementImage'
  titleKey = 'uploadRentAgreement',             // translation key
  documentType = 'Rent Agreement'               // fallback / display text
}) => {
  const { t } = useTranslation();
  const { values, touched, errors, setFieldValue, handleBlur } = useFormikContext();

  const [showModal, setShowModal] = useState(false);
  const [cameraActive, setCameraActive] = useState(false);
  const [cameraError, setCameraError] = useState(null);

  const videoRef = useRef(null);
  const canvasRef = useRef(null);
  const fileInputRef = useRef(null);

  const currentFile = values[fieldName];
  const oldFileUrl = values[oldFieldName];
  const hasFile = !!currentFile || !!oldFileUrl;

  const displayName = currentFile?.name || oldFileUrl || '';

  // ── Camera logic ───────────────────────────────────────────────────
  const openCamera = async () => {
    setCameraError(null);
    setCameraActive(true);

    try {
      let stream = await navigator.mediaDevices.getUserMedia({
        video: {
          facingMode: { ideal: 'environment' },
          width: { ideal: 1280 },
          height: { ideal: 720 },
        },
      });

      if (stream && stream.getVideoTracks().length > 0) {
        const track = stream.getVideoTracks()[0];
        const settings = track.getSettings();
        if (!settings.width || settings.width === 0) {
          stream.getTracks().forEach(t => t.stop());
          stream = await navigator.mediaDevices.getUserMedia({
            video: { facingMode: 'user', width: { ideal: 1280 }, height: { ideal: 720 } },
          });
        }
      }

      if (videoRef.current) {
        videoRef.current.srcObject = stream;
      }
    } catch (err) {
      setCameraError(
        err.name === 'NotAllowedError' || err.name === 'PermissionDeniedError'
          ? t('cameraPermissionDenied')
          : t('cameraErrorFallback')
      );
    }
  };

  const capturePhoto = () => {
    if (!videoRef.current || !canvasRef.current) return;

    const video = videoRef.current;
    canvasRef.current.width = video.videoWidth;
    canvasRef.current.height = video.videoHeight;

    const ctx = canvasRef.current.getContext('2d');
    ctx.drawImage(video, 0, 0, canvasRef.current.width, canvasRef.current.height);

    canvasRef.current.toBlob(
      (blob) => {
        if (!blob) return;
        const file = new File([blob], `${fieldName}-${Date.now()}.jpg`, { type: 'image/jpeg' });
        setFieldValue(fieldName, file);
        closeCamera();
        setShowModal(false);
      },
      'image/jpeg',
      0.82
    );
  };

  const closeCamera = () => {
    if (videoRef.current?.srcObject) {
      videoRef.current.srcObject.getTracks().forEach(track => track.stop());
      videoRef.current.srcObject = null;
    }
    setCameraActive(false);
    setCameraError(null);
  };

  useEffect(() => {
    return () => closeCamera();
  }, []);

  // ── File handling ──────────────────────────────────────────────────
  const handleGalleryClick = () => fileInputRef.current?.click();

  const handleFileChange = (e) => {
    const file = e.currentTarget.files?.[0];
    if (file) {
      setFieldValue(fieldName, file);
      closeCamera();
      setShowModal(false);
    }
    e.target.value = '';
  };

  const handleRemove = (e) => {
    e.stopPropagation();
    setFieldValue(fieldName, null);
  };

  const openModal = () => {
    setShowModal(true);
    closeCamera();
  };

  return (
    <>
      <div className="flex items-center gap-2 pb-2 border-b border-slate-100 dark:border-slate-700 pt-4">
        <span className="material-symbols-outlined text-primary">folder_open</span>
        <h3 className="text-[#0d141b] dark:text-white text-lg font-bold">{t('documents')}</h3>
      </div>

      <div className="relative">
        <div
          onClick={openModal}
          className={`p-6 border-2 cursor-pointer rounded-xl bg-slate-50 dark:bg-slate-800/50 transition-colors text-center group min-h-[180px] flex flex-col justify-center ${
            touched[fieldName] && errors[fieldName]
              ? 'border-red-500'
              : 'border-dashed border-slate-300 dark:border-slate-600 hover:bg-slate-100 dark:hover:bg-slate-800'
          }`}
        >
          {hasFile ? (
            <div className="flex flex-col items-center gap-4">
              <div className="flex items-center gap-3 p-3 rounded-lg border border-[#e7edf3] dark:border-slate-700 bg-white dark:bg-slate-800 w-full max-w-md mx-auto relative group/file">
                <div className="size-10 rounded bg-green-50 dark:bg-green-900/20 flex items-center justify-center text-green-600 dark:text-green-400">
                  <span className="material-symbols-outlined">description</span>
                </div>
                <div className="flex-1 text-left overflow-hidden">
                  <p className="text-xs font-medium text-[#0d141b] dark:text-white truncate">
                    {displayName}
                  </p>
                  <p className="text-xs text-green-600 dark:text-green-400 flex items-center gap-1 mt-0.5">
                    <span className="material-symbols-outlined text-[14px]">check_circle</span>
                    {t('uploadedSuccessfully')}
                  </p>
                </div>
                <button
                  type="button"
                  onClick={handleRemove}
                  className="text-slate-400 hover:text-red-600 dark:hover:text-red-400 transition-colors p-2 opacity-70 group-hover/file:opacity-100"
                >
                  <span className="material-symbols-outlined">delete</span>
                </button>
              </div>
            </div>
          ) : (
            <>
              <div className="size-12 bg-primary/10 rounded-full flex items-center justify-center mx-auto mb-4 group-hover:scale-110 transition-transform">
                <span className="material-symbols-outlined text-primary text-2xl">cloud_upload</span>
              </div>
              <p className="text-[#0d141b] dark:text-white font-semibold text-lg mb-1">
                {t(titleKey)}
              </p>
              <p className="text-[#4c739a] dark:text-slate-400 text-xs mb-4">
                {t('dragOrClickToUpload')}
              </p>
              <span className="inline-flex items-center gap-1 px-3 py-1 rounded-full bg-slate-200 dark:bg-slate-700 text-xs text-slate-600 dark:text-slate-300">
                PDF, JPG, PNG {t('maxSize')} 5MB
              </span>
            </>
          )}
        </div>

        <input
          ref={fileInputRef}
          type="file"
          accept=".pdf,image/jpeg,image/jpg,image/png"
          onChange={handleFileChange}
          onBlur={handleBlur}
          className="hidden"
        />
      </div>

      {touched[fieldName] && errors[fieldName] && (
        <p className="text-red-800 text-xs font-bold text-center mt-2">
          {errors[fieldName]}
        </p>
      )}

      {/* Modal */}
      {showModal && (
        <div className="fixed inset-0 z-50 flex items-center justify-center bg-black/60 backdrop-blur-sm p-4">
          <div className="bg-white dark:bg-gray-900 rounded-2xl shadow-2xl w-full max-w-md overflow-hidden">
            <div className="px-6 py-4 border-b border-gray-200 dark:border-gray-700 flex items-center justify-between">
              <h3 className="text-lg font-semibold text-gray-900 dark:text-white">
                {t(titleKey)}
              </h3>
              <button
                type="button"
                onClick={() => {
                  setShowModal(false);
                  closeCamera();
                }}
                className="text-gray-500 hover:text-gray-700 dark:text-gray-400 text-2xl leading-none"
              >
                ×
              </button>
            </div>

            <div className="p-6 space-y-6">
              {cameraActive ? (
                <div className="space-y-5">
                  <div className="relative bg-black rounded-xl overflow-hidden aspect-[4/3] border border-gray-700 shadow-inner">
                    <video
                      ref={videoRef}
                      className="w-full h-full object-cover"
                      autoPlay
                      playsInline
                      muted
                    />
                    <canvas ref={canvasRef} className="hidden" />
                  </div>

                  {cameraError && (
                    <p className="text-red-600 dark:text-red-400 text-sm text-center bg-red-50 dark:bg-red-950/30 p-3 rounded">
                      {cameraError}
                    </p>
                  )}

                  <div className="flex gap-4">
                    <button
                      type="button"
                      onClick={closeCamera}
                      className="flex-1 py-3 bg-gray-200 dark:bg-gray-700 text-gray-800 dark:text-gray-200 rounded-lg font-medium hover:bg-gray-300 dark:hover:bg-gray-600"
                    >
                      {t('cancel')}
                    </button>
                    <button
                      type="button"
                      onClick={capturePhoto}
                      disabled={!!cameraError}
                      className="flex-1 py-3 bg-green-600 text-white rounded-lg font-medium hover:bg-green-700 disabled:bg-gray-400 disabled:cursor-not-allowed"
                    >
                      {t('capturePhoto')}
                    </button>
                  </div>
                </div>
              ) : (
                <div className="grid grid-cols-2 gap-5">
                  <button
                    type="button"
                    onClick={openCamera}
                    className="flex flex-col items-center gap-3 py-8 border-2 border-dashed border-gray-400 dark:border-gray-600 rounded-xl hover:bg-gray-50 dark:hover:bg-gray-800 transition-colors"
                  >
                    <span className="material-symbols-outlined text-6xl text-blue-600">photo_camera</span>
                    <span className="font-medium text-gray-800 dark:text-gray-200">{t('takePhoto')}</span>
                  </button>

                  <button
                    type="button"
                    onClick={handleGalleryClick}
                    className="flex flex-col items-center gap-3 py-8 border-2 border-dashed border-gray-400 dark:border-gray-600 rounded-xl hover:bg-gray-50 dark:hover:bg-gray-800 transition-colors"
                  >
                    <span className="material-symbols-outlined text-6xl text-green-600">photo_library</span>
                    <span className="font-medium text-gray-800 dark:text-gray-200">{t('galleryOrFile')}</span>
                  </button>
                </div>
              )}
            </div>
          </div>
        </div>
      )}
    </>
  );
};

export default AgreementUpload;